import { ScrollView, View } from 'react-native'
import Typography from '@ca/ui/typography'
import Button from '@ca/ui/buttons/Button'
import { PanelBox } from './PanelBox'
import { Row } from './Row'
import { Phase, PdResult } from './types'
import { fmt } from './utils'
import { usePdFaceData } from './faceDataStore'

type Props = {
  phase: Phase
  result: PdResult | null
  onStart: () => void
  onStop: () => void
  onClear: () => void
  onOpenSettings: () => void
}

function PdGroup({
  title,
  pd,
  leftPd,
  rightPd,
}: {
  title: string
  pd: number | undefined
  leftPd: number | undefined
  rightPd: number | undefined
}) {
  return (
    <View className="gap-1">
      <Typography variant="XS" className="mb-1 text-black/30">
        {title}
      </Typography>
      <Row label="PD" value={`${fmt(pd, 2)} mm`} />
      <Row label="Left PD" value={`${fmt(leftPd, 2)} mm`} />
      <Row label="Right PD" value={`${fmt(rightPd, 2)} mm`} />
      <Row label="L+R sum" value={`${fmt(leftPd != null && rightPd != null ? leftPd + rightPd : undefined, 2)} mm`} />
    </View>
  )
}

function SettingsButton({ onPress }: { onPress: () => void }) {
  return (
    <View className="flex-row justify-end">
      <Button
        size="sm"
        variant="outlined"
        iconStart="settings"
        onPress={onPress}
      />
    </View>
  )
}

export function MeasureTab({
  phase,
  result,
  onStart,
  onStop,
  onClear,
  onOpenSettings,
}: Props) {
  const data = usePdFaceData()
  if (phase === 'idle') {
    return (
      <ScrollView
        className="flex-1"
        contentContainerClassName="flex-grow gap-3 px-4 py-3 pb-12"
      >
        <PanelBox className="flex-1 gap-3">
          <SettingsButton onPress={onOpenSettings} />
          {data ? (
            <>
              <PdGroup title="Live — Far" pd={data.pd} leftPd={data.leftPd} rightPd={data.rightPd} />
              <PdGroup title="Live — Near (offset)" pd={data.nearPd} leftPd={data.nearLeftPd} rightPd={data.nearRightPd} />
            </>
          ) : (
            <Typography variant="XS" className="text-black/40">
              Waiting for face…
            </Typography>
          )}
          <View className="mt-auto">
            <Button
              width="full"
              variant="filled"
              label="Start Measurement"
              onPress={onStart}
            />
          </View>
        </PanelBox>
      </ScrollView>
    )
  }

  if (phase === 'complete' && result) {
    return (
      <ScrollView
        className="flex-1"
        contentContainerClassName="flex-grow gap-3 px-4 py-3 pb-12"
      >
        <PanelBox className="flex-1 gap-3">
          <SettingsButton onPress={onOpenSettings} />
          <PdGroup title="Result — Far" pd={result.pd} leftPd={result.leftPd} rightPd={result.rightPd} />
          <PdGroup title="Result — Near (offset)" pd={result.nearPd} leftPd={result.nearLeftPd} rightPd={result.nearRightPd} />
          <View className="mt-auto">
            <Button
              width="full"
              variant="outlined"
              label="Clear"
              onPress={onClear}
            />
          </View>
        </PanelBox>
      </ScrollView>
    )
  }

  return (
    <ScrollView
      className="flex-1"
      contentContainerClassName="flex-grow gap-3 px-4 py-3 pb-12"
    >
      <PanelBox className="flex-1 gap-3">
        <SettingsButton onPress={onOpenSettings} />
        <View className="mt-auto">
          <Button
            width="full"
            variant="outlined"
            label="Cancel"
            onPress={onStop}
          />
        </View>
      </PanelBox>
    </ScrollView>
  )
}
